import React, { useContext, useEffect, useState } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { SearchPageContext } from '../../../SearchPageContainer/SearchPageContext.ts';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes.ts';
import PromoTooltip from './PromoTooltip';
import { setCookie } from '../../../../utils/Cookies/clientCookies.ts';
import { COOKIE_KEY } from '../../../../utils/Cookies/types.ts';
import ComparisonTooltip from './Tooltip';

type AddToComparisonProps = {
  dealCard: DealCard;
};

const MAX_COMPARISONS = 5;

const Content = ({
  canShowPromo,
  isSelected,
  handlePromoMessage,
}: {
  canShowPromo: boolean;
  isSelected: boolean;
  handlePromoMessage: (e: React.MouseEvent) => void;
}) => {
  const { t } = useTranslation();
  const promoTooltipContent = <PromoTooltip handlePromoMessage={handlePromoMessage} />;
  const remove = t('vacation.oss.comparison.tooltip.remove');
  const add = t('vacation.oss.comparison.tooltip.add');
  const tooltipContent = isSelected ? remove : add;
  return canShowPromo ? promoTooltipContent : <span>{tooltipContent}</span>;
};

const AddToComparison = ({ dealCard }: AddToComparisonProps) => {
  const { localization } = usePageWrapperData();
  const { isMobile } = useCommonClient();
  const { dealsToCompare, handleDealCompareAddClicked, canShowComparisonPromo, setCanShowComparisonPromo } =
    useContext(SearchPageContext);
  const [isPromoClicked, setIsPromoClicked] = useState(false);
  const hotelId = dealCard?.product?.accommodation?.hotelId;
  const isFirst = dealCard?.index === 0;
  const isSelected = !!dealsToCompare.find((d) => d.hotelId === hotelId);
  const maxReached = dealsToCompare.length >= MAX_COMPARISONS;
  const noSelected = dealsToCompare.length === 0;
  const isHidden = maxReached && !isSelected;

  const comparisonPromoEnabled = localization?.configValuesApp?.comparisonPromoEnabled;
  const canShowPromo = comparisonPromoEnabled && isFirst && noSelected && canShowComparisonPromo && !isPromoClicked;
  const tooltipSx = canShowPromo ? { padding: '12px 16px' } : { padding: '16px' };
  const promoTooltipOpen = canShowPromo && !isMobile;

  useEffect(() => {
    if (canShowPromo) {
      setCookie(COOKIE_KEY.COMPARISON_PROMO, 'true');
    }
  }, []);

  if (isHidden) return null;

  const handlePromoMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPromoClicked(true);
    setCanShowComparisonPromo(false);
  };

  const handleClick = (e: React.MouseEvent) => {
    handlePromoMessage(e);
    handleDealCompareAddClicked(dealCard.product.accommodation?.hotelId);
  };

  return (
    <ComparisonTooltip
      open={canShowPromo ? promoTooltipOpen : undefined}
      content={<Content canShowPromo={canShowPromo} isSelected={isSelected} handlePromoMessage={handlePromoMessage} />}
      tooltipSx={tooltipSx}
      handleClick={handleClick}
      isSelected={isSelected}
      isPromo={canShowPromo}
      isPromoButton={canShowPromo}
    />
  );
};

export default AddToComparison;
